"use client";

import { useCallback, useId, useState } from "react";

import type { PillCounterScanError, PillCounterScanSuccess } from "@/lib/pill-counter/api-types";
import { EVENTS } from "@/lib/analytics/trackEvent";
import { breadcrumbs } from "@/lib/logger/sentryBreadcrumb";
import { safeTrack } from "@/lib/safeTrack";

import { PillCountBadge } from "./PillCountBadge";

type Props = {
  previewUrl: string;
  accessToken: string | null;
  disabled: boolean;
  onAiSuccess: (result: PillCounterScanSuccess) => void;
  onSuggestedCount: (count: number) => void;
};

function isSuccess(json: unknown): json is PillCounterScanSuccess {
  return (
    typeof json === "object" &&
    json !== null &&
    "ok" in json &&
    (json as { ok?: unknown }).ok === true &&
    typeof (json as { pill_count?: unknown }).pill_count === "number"
  );
}

function isErr(json: unknown): json is PillCounterScanError {
  return typeof json === "object" && json !== null && "error" in json && typeof (json as { error?: unknown }).error === "string";
}

/** Decode data URLs without fetch — more reliable on mobile WebViews than fetch(data:…). */
function dataUrlToBlob(dataUrl: string): Blob | null {
  try {
    const comma = dataUrl.indexOf(",");
    if (comma < 0) return null;
    const header = dataUrl.slice(0, comma);
    const b64 = dataUrl.slice(comma + 1);
    const mimeMatch = /data:([^;]+)/.exec(header);
    const mime = (mimeMatch?.[1] || "image/jpeg").trim().toLowerCase();
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime || "image/jpeg" });
  } catch {
    return null;
  }
}

function friendlyApiMessage(json: PillCounterScanError | null, httpStatus: number): string {
  const code = json?.code;
  if (code === "PILL_COUNTER_RATE_LIMIT") {
    return "You’ve reached the AI scan limit for this hour. Try again soon.";
  }
  if (code === "ANTHROPIC_NOT_CONFIGURED") {
    return "AI counting isn’t available right now. Please try again later.";
  }
  if (code === "VISION_FAILED" || code === "VISION_PARSE_FAILED") {
    return "Couldn’t read this image clearly — try brighter light or a closer, steadier shot.";
  }
  if (code === "DB_INSERT_FAILED") {
    return "Your count is ready but we couldn’t save it to your log. Try again — if it keeps happening, let us know.";
  }
  if (code === "IMAGE_TOO_LARGE" || code === "UNSUPPORTED_MEDIA") {
    return json?.error ?? "Image format or size isn’t supported.";
  }
  if (httpStatus === 401) return "Session expired — refresh the page and sign in again.";
  if (httpStatus === 429) return "Too many requests. Wait a moment and try again.";
  if (json?.error) return json.error;
  return "Something went wrong. Try again.";
}

/**
 * AI detection controls (no image — parent owns the preview frame and dot overlay).
 */
export function PillCounter({ previewUrl, accessToken, disabled, onAiSuccess, onSuggestedCount }: Props) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<PillCounterScanSuccess | null>(null);
  const statusId = useId();
  const errId = useId();

  const runAi = useCallback(async () => {
    setErr(null);
    if (!accessToken) {
      setErr("Sign in again to use AI detection.");
      return;
    }
    setLoading(true);
    setResult(null);
    breadcrumbs.pillScanStarted();
    safeTrack(
      async () => {
        await fetch("/api/analytics/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventName: EVENTS.PILL_SCAN_STARTED,
            properties: {},
          }),
          keepalive: true,
        });
      },
      "pill_counter_scan_started",
    );
    try {
      let blob: Blob | null = null;
      if (previewUrl.startsWith("data:")) {
        blob = dataUrlToBlob(previewUrl);
      } else {
        const res = await fetch(previewUrl);
        if (!res.ok) {
          setErr("Could not read this image — capture or upload again.");
          return;
        }
        blob = await res.blob();
      }
      if (!blob || blob.size <= 0) {
        setErr("Could not read this image — try another photo.");
        return;
      }
      const fd = new FormData();
      const ext =
        blob.type === "image/png" ? "png" : blob.type === "image/webp" ? "webp" : blob.type === "image/gif" ? "gif" : "jpg";
      fd.set("image", blob, `pill-counter.${ext}`);

      const out = await fetch("/api/pill-counter", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: fd,
      });

      let json: unknown;
      try {
        json = await out.json();
      } catch {
        setErr("Something didn't work as expected. Try again.");
        return;
      }

      if (!out.ok) {
        const parsedErr = isErr(json) ? json : null;
        setErr(friendlyApiMessage(parsedErr, out.status));
        return;
      }
      if (!isSuccess(json)) {
        setErr("Something didn't work as expected. Try again.");
        return;
      }
      setResult(json);
      breadcrumbs.pillScanCompleted(json.pill_count, json.confidence);
      safeTrack(
        async () => {
          await fetch("/api/analytics/track", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              eventName: EVENTS.PILL_SCAN_COMPLETED,
              properties: {
                pillCount: json.pill_count,
                confidence: json.confidence,
                warningCount: Array.isArray(json.warnings) ? json.warnings.length : 0,
                scanId: json.scan_id,
              },
            }),
            keepalive: true,
          });
        },
        "pill_counter_scan_completed",
      );
      onAiSuccess(json);
      if (json.pill_count > 0) {
        onSuggestedCount(json.pill_count);
      }
    } catch (error) {
      const errorMessage = "Network error — check your connection and try again.";
      setErr(errorMessage);
      breadcrumbs.pillScanFailed(error instanceof Error ? error.message : errorMessage);
      safeTrack(
        async () => {
          await fetch("/api/analytics/track", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              eventName: EVENTS.PILL_SCAN_FAILED,
              properties: { reason: error instanceof Error ? error.message : errorMessage },
            }),
            keepalive: true,
          });
        },
        "pill_counter_scan_failed",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, previewUrl, onAiSuccess, onSuggestedCount]);

  return (
    <section className="ed-pill-ai" aria-labelledby={statusId}>
      <p id={statusId} className="ed-pill-ai__eyebrow">
        AI assist <span className="ed-pill-ai__eyebrow-muted">(optional)</span>
      </p>
      <div className="ed-pill-ai__toolbar">
        <button
          type="button"
          className="ed-scan-primary ed-pill-ai__btn"
          disabled={disabled || loading || !accessToken}
          aria-busy={loading}
          aria-describedby={err ? errId : undefined}
          onClick={() => void runAi()}
        >
          {loading ? "Detecting pills…" : "Detect pills with AI"}
        </button>
        {result ? <PillCountBadge count={result.pill_count} confidence={result.confidence} /> : null}
      </div>

      <div className="ed-pill-ai__live" aria-live="polite">
        {loading ? <span className="ed-visually-hidden">Running AI detection</span> : null}
        {result && !loading ? (
          <span className="ed-visually-hidden">
            Detection finished: {result.pill_count} {result.pill_count === 1 ? "pill" : "pills"}.
          </span>
        ) : null}
      </div>

      {result?.detected_medicine_name ? (
        <p className="ed-pill-ai__packaging">
          <strong>Packaging text:</strong> {result.detected_medicine_name}
        </p>
      ) : null}

      {result?.med_match ? (
        <p className="ed-pill-ai__catalog">
          <strong>Catalog match:</strong> {result.med_match.canonical_name}{" "}
          <span className="ed-pill-ai__catalog-meta">
            ({result.med_match.match_confidence} · score {result.med_match.score.toFixed(2)})
          </span>
        </p>
      ) : null}

      {result?.warnings?.length ? (
        <ul className="ed-pill-ai__warnings">
          {result.warnings.map((w, i) => (
            <li key={`${i}-${w.slice(0, 24)}`}>{w}</li>
          ))}
        </ul>
      ) : null}

      {result && result.persisted === false ? (
        <p className="ed-pill-ai__persist-hint" role="status">
          Your count is ready, but saving it failed. This has been reported automatically.
        </p>
      ) : null}

      {err ? (
        <p id={errId} className="ed-pill-tool__err" role="alert">
          {err}
        </p>
      ) : null}
    </section>
  );
}
